import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, Chip, Dialog,
  DialogActions, DialogContent, DialogTitle, IconButton, Paper, Skeleton, Snackbar,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, ToggleButton, ToggleButtonGroup, Typography,
  useMediaQuery, useTheme, Collapse, Badge } from '@mui/material';
import {
  AreaChart, Area, BarChart, Bar, CartesianGrid, Cell, ComposedChart, Legend,
  Line, LineChart, PieChart, Pie, ResponsiveContainer, Tooltip as RechartsTooltip,
  XAxis, YAxis,
} from 'recharts';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FilterListIcon from '@mui/icons-material/FilterList';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import Layout from '../components/Layout';
import {
  fetchDashboardFilters, fetchDashboardKpis, fetchTopMovers, fetchSupplierScorecard,
  fetchCategoryBreakdown, fetchSpendTimeline, fetchAnomalies,
  fetchDashboardArticleDetail, fetchTagespreisCorridor, fetchAlerts, updateAlertStatus,
  generateAlertWhatsApp, fetchSupplierContacts, saveSupplierContact,
  DashboardKpis, Mover, SupplierScoreRow, CategoryRow, SpendBucket, Anomaly,
  DashboardArticleDetail, Corridor, DashboardFilters, PriceAlert,
} from '../api/client';
import {
  InvoicePdfButton, NichtNormalisiertChip, ProvenanceTooltip, fmtEURUnit,
} from '../components/LineProvenance';

// ----------------------------- Format helpers ---------------------------------

const fmtEUR = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
const fmtNum = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('de-DE');
const fmtDate = (s: string | null | undefined) =>
  !s ? '—' : new Date(s).toLocaleDateString('de-DE');
const fmtPct = (n: number | null | undefined, sign = false) =>
  n == null ? '—' : `${sign && n > 0 ? '+' : ''}${n.toFixed(1)}%`;

// 12 evenly spaced category colours
const CAT_COLORS = [
  '#2E7D32', '#1565C0', '#EF6C00', '#6A1B9A', '#00838F', '#C62828',
  '#558B2F', '#283593', '#AD1457', '#4527A0', '#00695C', '#E65100',
];

const DEFAULT_WINDOW: '7' | '30' | '90' | '180' | '365' = '180';

// ----------------------------- Page -------------------------------------------

export default function OwnerDashboardPage() {
  // -------- Global filter state
  const [windowDays, setWindowDays] = useState<string>(DEFAULT_WINDOW);
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [filterOptions, setFilterOptions] = useState<{ suppliers: string[]; categories: string[] }>({
    suppliers: [], categories: [],
  });

  const filters: DashboardFilters & { window_days?: number } = useMemo(() => {
    const today = new Date();
    const from = new Date();
    from.setDate(today.getDate() - parseInt(windowDays, 10));
    const f: any = {
      date_from: from.toISOString().slice(0, 10),
      date_to: today.toISOString().slice(0, 10),
      window_days: parseInt(windowDays, 10),
    };
    if (selectedSuppliers.length) f.suppliers = selectedSuppliers.join(',');
    if (selectedCategories.length) f.categories = selectedCategories.join(',');
    return f;
  }, [windowDays, selectedSuppliers, selectedCategories]);

  // -------- Block data
  const [kpis, setKpis] = useState<DashboardKpis | null>(null);
  const [risers, setRisers] = useState<Mover[]>([]);
  const [fallers, setFallers] = useState<Mover[]>([]);
  const [scorecard, setScorecard] = useState<SupplierScoreRow[]>([]);
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [timeline, setTimeline] = useState<SpendBucket[]>([]);
  const [anomalies, setAnomalies] = useState<Anomaly[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  // -------- Article detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState<DashboardArticleDetail | null>(null);
  const [corridor, setCorridor] = useState<Corridor | null>(null);

  const loadFilters = useCallback(async () => {
    try {
      const opts = await fetchDashboardFilters();
      setFilterOptions(opts);
    } catch {/* non-fatal */}
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [k, tm, sc, cb, st, an] = await Promise.all([
        fetchDashboardKpis(filters),
        fetchTopMovers(filters),
        fetchSupplierScorecard(filters),
        fetchCategoryBreakdown(filters),
        fetchSpendTimeline({ ...filters, bucket: 'week' }),
        fetchAnomalies(filters),
      ]);
      setKpis(k);
      setRisers(tm.risers);
      setFallers(tm.fallers);
      setScorecard(sc);
      setCategories(cb);
      setTimeline(st);
      setAnomalies(an);
    } catch (e: any) {
      // FastAPI 422 puts an ARRAY of objects in .detail — rendering that crashes React (#31)
      const d = e?.response?.data?.detail;
      setErr(typeof d === 'string' ? d : 'Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadFilters(); }, [loadFilters]);
  useEffect(() => { loadAll(); }, [loadAll]);

  const openDetail = async (m: Mover) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    setCorridor(null);
    try {
      const params = m.cluster_id
        ? { cluster_id: m.cluster_id }
        : { supplier: m.supplier, article_no: m.article_no };
      const d = await fetchDashboardArticleDetail(params);
      setDetail(d);
      if (d.cluster_id && d.is_tagespreis) {
        try {
          const c = await fetchTagespreisCorridor({ cluster_id: d.cluster_id });
          setCorridor(c);
        } catch { /* corridor optional */ }
      }
    } catch (e: any) {
      // dialog will show empty state
    } finally {
      setDetailLoading(false);
    }
  };

  const resetFilters = () => {
    setWindowDays(DEFAULT_WINDOW);
    setSelectedSuppliers([]);
    setSelectedCategories([]);
  };

  // -------- Supplier drill-down: scorecard row -> that supplier's open alerts
  const [allAlerts, setAllAlerts] = useState<PriceAlert[] | null>(null);
  const [supAlertsOpen, setSupAlertsOpen] = useState(false);
  const [supAlertsFor, setSupAlertsFor] = useState('');
  const [supAlerts, setSupAlerts] = useState<PriceAlert[]>([]);
  const [supAlertsLoading, setSupAlertsLoading] = useState(false);

  const openSupplierAlerts = async (supplier: string) => {
    setSupAlertsFor(supplier);
    setSupAlertsOpen(true);
    setSupAlertsLoading(true);
    try {
      let alerts = allAlerts;
      if (!alerts) {
        alerts = await fetchAlerts('open', 500);
        setAllAlerts(alerts);
      }
      setSupAlerts(
        alerts
          .filter(a => a.supplier === supplier)
          .sort((a, b) => (b.yearly_impact_eur || 0) - (a.yearly_impact_eur || 0)),
      );
    } catch {
      setSupAlerts([]);
    } finally {
      setSupAlertsLoading(false);
    }
  };

  // supplier -> WhatsApp number (shared directory, grows through use)
  const [contacts, setContacts] = useState<Record<string, string>>({});
  useEffect(() => { fetchSupplierContacts().then(setContacts).catch(() => {}); }, []);
  const [waAsk, setWaAsk] = useState<PriceAlert | null>(null);
  const [waPhone, setWaPhone] = useState('');

  const sendAlertWhatsApp = async (a: PriceAlert, phone: string) => {
    if (!a.id) return;
    const res = await generateAlertWhatsApp(a.id, phone);
    window.open(res.phone_url, '_blank');
  };

  const openAlertWhatsApp = (a: PriceAlert) => {
    const phone = contacts[a.supplier];
    if (phone) {
      sendAlertWhatsApp(a, phone);
    } else {
      setWaAsk(a);
      setWaPhone('');
    }
  };

  const confirmWaPhone = async () => {
    if (!waAsk) return;
    const digits = waPhone.replace(/\D/g, '');
    if (digits.length < 8) return;
    try {
      await saveSupplierContact(waAsk.supplier, digits);
      setContacts(prev => ({ ...prev, [waAsk.supplier]: digits }));
    } catch { /* trotzdem senden */ }
    await sendAlertWhatsApp(waAsk, digits);
    setWaAsk(null);
    setWaPhone('');
  };

  const setAlertStatus = async (a: PriceAlert, status: 'resolved' | 'dismissed') => {
    if (!a.id) return;
    await updateAlertStatus(a.id, status);
    setSupAlerts(prev => prev.filter(x => x.id !== a.id));
    setAllAlerts(prev => (prev ? prev.filter(x => x.id !== a.id) : prev));
  };

  const openAlertDetail = (a: PriceAlert) => {
    // Prefer the cluster: the detail then shows ALL suppliers' lines for the
    // product — including the cheaper competitor the alert compares against.
    openDetail({ cluster_id: a.cluster_id || undefined,
                 supplier: a.supplier, article_no: a.article_no } as Mover);
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const activeFilters = selectedSuppliers.length + selectedCategories.length;

  // ----- Renderers ----

  return (
    <Layout>
      <Box sx={{ mb: { xs: 1.5, md: 3 } }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700} gutterBottom={!isMobile}>Dashboard</Typography>
        {!isMobile && (
          <Typography variant="body2" color="text.secondary">
            Übersicht aller Ausgaben, Preisbewegungen, Lieferanten und Kategorien — interaktiv und filterbar.
          </Typography>
        )}
      </Box>

      {/* Filter bar */}
      <Card sx={{ mb: { xs: 1.5, md: 3 } }}>
        <CardContent sx={{ py: { xs: 1, md: 2 }, '&:last-child': { pb: { xs: 1, md: 2 } } }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ToggleButtonGroup
              size="small" exclusive value={windowDays}
              onChange={(_, v) => v && setWindowDays(v)}
              sx={{ flexWrap: 'nowrap', overflowX: 'auto' }}
            >
              <ToggleButton value="7">7T</ToggleButton>
              <ToggleButton value="30">30T</ToggleButton>
              <ToggleButton value="90">90T</ToggleButton>
              <ToggleButton value="180">6M</ToggleButton>
              <ToggleButton value="365">1J</ToggleButton>
            </ToggleButtonGroup>
            <Box sx={{ flex: 1 }} />
            <Badge badgeContent={activeFilters} color="primary">
              <IconButton size="small" onClick={() => setFiltersOpen(o => !o)} aria-label="Filter">
                <FilterListIcon />
              </IconButton>
            </Badge>
          </Stack>
          <Collapse in={filtersOpen}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mt: 1.5 }} alignItems={{ md: 'center' }}>
              <Autocomplete
                multiple size="small" options={filterOptions.suppliers}
                value={selectedSuppliers} onChange={(_, v) => setSelectedSuppliers(v)}
                renderInput={(p) => <TextField {...p} label="Lieferanten" />}
                sx={{ minWidth: 240, flex: 1 }}
              />
              <Autocomplete
                multiple size="small" options={filterOptions.categories}
                value={selectedCategories} onChange={(_, v) => setSelectedCategories(v)}
                renderInput={(p) => <TextField {...p} label="Kategorien" />}
                sx={{ minWidth: 240, flex: 1 }}
              />
              <Button size="small" startIcon={<RestartAltIcon />} onClick={resetFilters}>
                Zurücksetzen
              </Button>
            </Stack>
          </Collapse>
        </CardContent>
      </Card>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

      {/* KPI strip */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <KpiCard label="Sparpotenzial / Jahr" value={fmtEUR(kpis?.sparpotenzial_eur_yearly)} accent="success" loading={loading} />
        <KpiCard label="Ausgaben gesamt" value={fmtEUR(kpis?.total_spend_eur)} loading={loading} />
        <KpiCard label="Ø Überzahlung" value={fmtPct(kpis?.avg_overcharge_pct, true)} accent={kpis?.avg_overcharge_pct && kpis.avg_overcharge_pct > 0 ? 'warning' : undefined} loading={loading} />
        <KpiCard label="Zu viel bezahlt (90 Tage)" value={fmtEUR(kpis?.retro_overpaid_eur)} accent={kpis?.retro_overpaid_eur ? 'warning' : undefined} loading={loading} />
      </Box>

      {/* Top Movers row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 3 }}>
        <MoversCard title="Top 5 Preisanstiege" icon={<TrendingUpIcon />} movers={risers} loading={loading}
                    color="warning" onPick={openDetail} />
        <MoversCard title="Top 5 Preissenkungen" icon={<TrendingDownIcon />} movers={fallers} loading={loading}
                    color="success" onPick={openDetail} />
      </Box>

      {/* Anomalies feed */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <ReportProblemIcon color="warning" />
            <Typography variant="h6" fontWeight={700}>Anomalien</Typography>
          </Stack>
          {loading ? <Skeleton variant="rounded" height={80} /> :
            anomalies.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Keine Anomalien im Filterbereich.</Typography>
            ) : (
              <Stack spacing={1}>
                {anomalies.slice(0, 6).map((a, i) => (
                  <Box key={i} sx={{ p: 1.5, borderRadius: 1, bgcolor: a.severity === 'critical' ? 'error.lighter' : 'warning.lighter', border: '1px solid', borderColor: 'divider' }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start">
                      <Chip size="small" color={a.severity === 'critical' ? 'error' : 'warning'}
                            label={a.severity === 'critical' ? 'KRITISCH' : 'Warnung'} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={600}>{a.title}</Typography>
                        <Typography variant="caption" color="text.secondary">{a.detail}</Typography>
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )
          }
        </CardContent>
      </Card>

      {/* Charts row */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2, mb: 3 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={700} gutterBottom>Ausgaben-Verlauf (wöchentlich)</Typography>
            {loading ? <Skeleton variant="rounded" height={260} /> : (
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={timeline}>
                  <defs>
                    <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#1565C0" stopOpacity={0.6} />
                      <stop offset="95%" stopColor="#1565C0" stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="period_start" tickFormatter={(v) => fmtDate(v)} fontSize={11} />
                  <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} fontSize={11} />
                  <RechartsTooltip
                    formatter={(v: any) => fmtEUR(Number(v))}
                    labelFormatter={(l) => `KW ${fmtDate(l as string)}`}
                  />
                  <Area type="monotone" dataKey="total_spend" stroke="#1565C0" strokeWidth={2} fill="url(#spendGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <Typography variant="h6" fontWeight={700} gutterBottom>Ausgaben pro Kategorie</Typography>
            {loading ? <Skeleton variant="rounded" height={260} /> : categories.length === 0 ? (
              <Typography variant="body2" color="text.secondary">Keine kategorisierten Daten.</Typography>
            ) : (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={categories} dataKey="total_spend" nameKey="category"
                       cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={2}>
                    {categories.map((_, i) => <Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length]} />)}
                  </Pie>
                  <RechartsTooltip formatter={(v: any, n: any) => [fmtEUR(Number(v)), n]} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Supplier scorecard */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>Lieferanten-Scorecard</Typography>
          {loading ? <Skeleton variant="rounded" height={180} /> : (
            <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 720 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Lieferant</TableCell>
                    <TableCell align="right">Ausgaben</TableCell>
                    <TableCell align="right">Anteil</TableCell>
                    <TableCell align="right">Ø Überzahlung</TableCell>
                    <TableCell align="center">Warnungen</TableCell>
                    <TableCell align="right">Rechnungen</TableCell>
                    <TableCell align="right">Artikel</TableCell>
                    <TableCell>Letzte</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scorecard.map(s => (
                    <TableRow
                      key={s.supplier} hover
                      onClick={() => openSupplierAlerts(s.supplier)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{s.supplier}</TableCell>
                      <TableCell align="right">{fmtEUR(s.total_spend)}</TableCell>
                      <TableCell align="right">{fmtPct(s.share_of_spend_pct)}</TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color={s.avg_overcharge_pct > 0 ? 'warning.main' : 'text.primary'}>
                          {fmtPct(s.avg_overcharge_pct, true)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {s.open_alerts ? <Chip size="small" color="warning" label={s.open_alerts} /> : '—'}
                      </TableCell>
                      <TableCell align="right">{s.invoice_count}</TableCell>
                      <TableCell align="right">{s.article_count}</TableCell>
                      <TableCell>{fmtDate(s.last_invoice_date)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Category breakdown table */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} gutterBottom>Kategorien-Übersicht</Typography>
          {loading ? <Skeleton variant="rounded" height={180} /> : (
            <ResponsiveContainer width="100%" height={Math.max(180, categories.length * 28 + 40)}>
              <BarChart data={categories} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis type="number" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} fontSize={11} />
                <YAxis type="category" dataKey="category" width={150} fontSize={11} />
                <RechartsTooltip formatter={(v: any) => fmtEUR(Number(v))} />
                <Bar dataKey="total_spend" fill="#1565C0" radius={[0, 4, 4, 0]}>
                  {categories.map((_, i) => <Cell key={i} fill={CAT_COLORS[i % CAT_COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Article detail dialog */}
      {/* Supplier alerts drill-down (scorecard row click) */}
      <Dialog open={supAlertsOpen} onClose={() => setSupAlertsOpen(false)} fullWidth maxWidth="md" fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ minWidth: 0, flex: 1 }}>
            <ReportProblemIcon color="warning" />
            <Box component="span" sx={{ wordBreak: 'break-word' }}>Warnungen — {supAlertsFor}</Box>
            {!supAlertsLoading && <Chip size="small" color="warning" label={supAlerts.length} />}
          </Stack>
          <IconButton onClick={() => setSupAlertsOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {supAlertsLoading ? <Skeleton variant="rounded" height={200} /> : supAlerts.length === 0 ? (
            <Alert severity="success">Keine offenen Warnungen für diesen Lieferanten.</Alert>
          ) : (
            <Stack spacing={1.5}>
              {supAlerts.map((a, i) => (
                <Card
                  key={a.id ?? i} variant="outlined"
                  onClick={() => openAlertDetail(a)}
                  sx={{ cursor: 'pointer', '&:hover': { borderColor: 'primary.main', boxShadow: 1 } }}
                >
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mb: 0.5 }}>
                      <Chip
                        size="small"
                        color={a.alert_type === 'same_supplier' ? 'warning' : 'primary'}
                        label={a.alert_type === 'same_supplier' ? 'Preis gestiegen' : 'Woanders günstiger'}
                      />
                      <Chip size="small" variant="outlined"
                        label={fmtPct(a.delta_pct, true)} />
                      <Typography variant="subtitle2" fontWeight={700}>
                        {a.cluster_name || a.article_name}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {fmtEUR(a.current_price)}{a.normalized_unit ? `/${a.normalized_unit}` : ''} statt{' '}
                      <strong>{fmtEUR(a.best_price)}{a.normalized_unit ? `/${a.normalized_unit}` : ''}</strong>
                      {a.alert_type === 'same_supplier'
                        ? ` (eigener Bestpreis${a.best_price_date ? ` vom ${fmtDate(a.best_price_date)}` : ''})`
                        : a.best_supplier ? ` bei ${a.best_supplier}` : ''}
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 0.5 }}>
                      {a.retro_overpaid_eur != null && a.retro_overpaid_eur > 0 && (
                        <Typography variant="caption" color="error.main">
                          Bereits {fmtEUR(a.retro_overpaid_eur)} zu viel bezahlt
                          {a.retro_from ? ` (seit ${fmtDate(a.retro_from)})` : ''}
                        </Typography>
                      )}
                      <Typography variant="caption" color="success.main">
                        Sparpotenzial {fmtEUR(a.yearly_impact_eur)}/Jahr
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Button size="small" color="success" variant="contained" startIcon={<WhatsAppIcon />}
                        onClick={(e) => { e.stopPropagation(); openAlertWhatsApp(a); }}>
                        WhatsApp
                      </Button>
                      <Button size="small" onClick={(e) => { e.stopPropagation(); setAlertStatus(a, 'resolved'); }}>
                        Geklärt
                      </Button>
                      <Button size="small" color="inherit" onClick={(e) => { e.stopPropagation(); setAlertStatus(a, 'dismissed'); }}>
                        Ignorieren
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSupAlertsOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!waAsk} onClose={() => setWaAsk(null)} fullWidth maxWidth="xs">
        <DialogTitle>WhatsApp-Nummer von {waAsk?.supplier}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Für diesen Lieferanten ist noch keine Nummer hinterlegt. Einmal eintragen — dann geht es beim nächsten Mal direkt.
          </Typography>
          <TextField
            autoFocus fullWidth label="Nummer (international, z. B. 4915112345678)"
            value={waPhone} onChange={(e) => setWaPhone(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaAsk(null)}>Abbrechen</Button>
          <Button variant="contained" disabled={waPhone.replace(/\D/g, '').length < 8} onClick={confirmWaPhone}>
            Speichern & senden
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="md" fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, pr: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ minWidth: 0, flex: 1 }}>
            <ShowChartIcon color="primary" />
            <Box component="span" sx={{ wordBreak: 'break-word' }}>{detail?.cluster_name || 'Artikel-Details'}</Box>
            {detail?.is_tagespreis && <Chip size="small" color="info" label="Tagespreis" />}
            {detail?.category && <Chip size="small" variant="outlined" label={detail.category} />}
          </Stack>
          <IconButton onClick={() => setDetailOpen(false)} size="small" sx={{ flexShrink: 0 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? <Skeleton variant="rounded" height={300} /> : !detail ? (
            <Alert severity="info">Keine Details verfügbar.</Alert>
          ) : <ArticleDetailBody d={detail} corridor={corridor} />}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

// ----------------------------- Sub-components ---------------------------------

function KpiCard({ label, value, accent, loading }: {
  label: string; value: string; accent?: 'success' | 'warning'; loading: boolean;
}) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
        {loading ? <Skeleton width={80} height={32} /> : (
          <Typography variant="h6" fontWeight={700}
            color={accent === 'success' ? 'success.main' : accent === 'warning' ? 'warning.main' : 'text.primary'}>
            {value}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function MoversCard({ title, icon, movers, loading, color, onPick }: {
  title: string; icon: React.ReactNode; movers: Mover[]; loading: boolean;
  color: 'warning' | 'success'; onPick: (m: Mover) => void;
}) {
  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Box sx={{ color: `${color}.main`, display: 'flex' }}>{icon}</Box>
          <Typography variant="h6" fontWeight={700}>{title}</Typography>
        </Stack>
        {loading ? <Skeleton variant="rounded" height={250} /> :
          movers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Keine Bewegungen im Filterbereich.</Typography>
          ) : (
            <Stack spacing={1}>
              {movers.map(m => (
                <Box key={`${m.supplier}::${m.article_no}`} onClick={() => onPick(m)}
                  sx={{
                    p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider',
                    cursor: 'pointer', '&:hover': { borderColor: `${color}.main`, bgcolor: `${color}.lighter` },
                  }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Chip size="small" color={color} label={fmtPct(m.delta_pct, true)} />
                    <Typography variant="caption" color="text.secondary">{m.supplier}</Typography>
                  </Stack>
                  <Typography variant="body2" fontWeight={600} noWrap>{m.cluster_name}</Typography>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
                    <Typography variant="caption">
                      {fmtEUR(m.baseline_price)} → <strong>{fmtEUR(m.latest_price)}</strong> / {m.normalized_unit || 'Einheit'}
                    </Typography>
                    <Typography variant="caption" color={`${color}.main`}>
                      ≈ {fmtEUR(m.estimated_yearly_impact)}/J
                    </Typography>
                    {m.sparkline.length > 1 && (
                      <Box sx={{ flex: 1, minWidth: 80, height: 32 }}>
                        <ResponsiveContainer>
                          <LineChart data={m.sparkline} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                            {/* Hidden axis with a little headroom so a single jump
                                doesn't flatten the rest of the line against an edge. */}
                            <YAxis hide domain={['dataMin', 'dataMax']} padding={{ top: 3, bottom: 3 }} />
                            <Line type="monotone" dataKey="unit_price_per_einheit"
                                  stroke={color === 'success' ? '#2E7D32' : '#EF6C00'}
                                  strokeWidth={1.75} dot={false} connectNulls
                                  isAnimationActive={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    )}
                  </Stack>
                </Box>
              ))}
            </Stack>
          )
        }
      </CardContent>
    </Card>
  );
}

function ArticleDetailBody({ d, corridor }: {
  d: DashboardArticleDetail; corridor: Corridor | null;
}) {
  const [pdfNotice, setPdfNotice] = useState<string | null>(null);
  const chart = useMemo(() => {
    return (d.history || []).map(h => ({
      date: h.date,
      price: h.unit_price_per_einheit ?? h.unit_price,
      qty: h.quantity_normalized,
      supplier: h.supplier,
    }));
  }, [d]);

  return (
    <Stack spacing={2}>
      {/* 4 KPI cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
        <DialogKpi label={`Aktuell (${d.normalized_unit || 'Einheit'})`}
          value={`${fmtEUR(d.current_price_min)} – ${fmtEUR(d.current_price_max)}`} />
        <DialogKpi label="Bester Preis (jemals)"
          value={`${fmtEUR(d.min_ever_price)}`} sub={`${d.min_ever_supplier || ''} · ${fmtDate(d.min_ever_date)}`} />
        <DialogKpi label="Bestellte Menge (Zeitraum)"
          value={`${fmtNum(Math.round(d.total_qty_normalized))} ${d.normalized_unit || ''}`} />
        <DialogKpi label="Hätte gespart"
          value={fmtEUR(d.would_have_saved)} accent="success"
          sub={`bei Bestpreis · Ausgaben ${fmtEUR(d.total_spend_window)}`} />
      </Box>

      {/* Dual-axis chart */}
      {chart.length > 0 && (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>Preis &amp; Menge im Zeitverlauf</Typography>
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="date" tickFormatter={(v) => fmtDate(v)} fontSize={11} />
              <YAxis yAxisId="left" tickFormatter={(v) => fmtEUR(v)} fontSize={11} />
              <YAxis yAxisId="right" orientation="right" fontSize={11} />
              <RechartsTooltip
                labelFormatter={(l) => fmtDate(l as string)}
                formatter={(v: any, key: any) => key === 'price' ? fmtEUR(Number(v)) : fmtNum(Number(v))}
              />
              <Legend />
              <Bar yAxisId="right" dataKey="qty" name="Menge" fill="#90A4AE" opacity={0.6} />
              <Line yAxisId="left" type="monotone" dataKey="price" name={`€ pro ${d.normalized_unit || 'Einheit'}`}
                    stroke="#1565C0" strokeWidth={2.5} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      )}

      {/* Tagespreis corridor (if applicable) */}
      {corridor && corridor.buckets.length > 0 && (
        <Box>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Tagespreis-Korridor (wöchentlich Min/Median/Max)
          </Typography>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={corridor.buckets}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="week_start" tickFormatter={(v) => fmtDate(v)} fontSize={11} />
              <YAxis tickFormatter={(v) => fmtEUR(v)} fontSize={11} />
              <RechartsTooltip
                labelFormatter={(l) => `KW ${fmtDate(l as string)}`}
                formatter={(v: any) => fmtEUR(Number(v))}
              />
              <Area type="monotone" dataKey="max_price" name="Höchstpreis" stroke="#EF6C00" fill="#EF6C00" fillOpacity={0.1} />
              <Area type="monotone" dataKey="min_price" name="Tiefstpreis" stroke="#2E7D32" fill="#2E7D32" fillOpacity={0.1} />
              <Line type="monotone" dataKey="median_price" name="Median" stroke="#1565C0" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      )}

      {/* All-time history table */}
      <Box>
        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Alle Rechnungspositionen</Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell>Datum</TableCell>
                <TableCell>Lieferant</TableCell>
                <TableCell>Rechnung</TableCell>
                <TableCell align="right">Menge</TableCell>
                <TableCell align="right">Preis/Einheit</TableCell>
                <TableCell align="right">Linien-Summe</TableCell>
                <TableCell align="center" sx={{ width: 44 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {(d.history || []).slice().reverse().slice(0, 30).map((h, i) => (
                <TableRow key={h.line_id ?? i} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(h.date)}</TableCell>
                  <TableCell>{h.supplier}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="body2">
                        {h.invoice_no || (h.invoice_id != null ? `#${h.invoice_id}` : '—')}
                      </Typography>
                      <InvoicePdfButton invoiceId={h.invoice_id} invoiceNo={h.invoice_no} onNotice={setPdfNotice} />
                    </Stack>
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {fmtNum(h.quantity)} {h.unit || ''}{h.inhalt_count && h.inhalt_count > 1 ? ` × ${h.inhalt_count}` : ''}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {h.unit_price_per_einheit != null
                      ? `${fmtEURUnit(h.unit_price_per_einheit)}/${h.normalized_unit || d.normalized_unit || 'Einheit'}`
                      : <NichtNormalisiertChip />}
                  </TableCell>
                  <TableCell align="right">{fmtEUR(h.total_eur)}</TableCell>
                  <TableCell align="center" sx={{ p: 0 }}>
                    <ProvenanceTooltip line={{
                      ...h,
                      total: h.total_eur,
                      // base_quantity is the newer field; quantity_normalized is the legacy name for the same thing
                      base_quantity: h.base_quantity ?? h.quantity_normalized,
                      normalized_unit: h.normalized_unit || d.normalized_unit,
                    }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Snackbar open={!!pdfNotice} autoHideDuration={4000} onClose={() => setPdfNotice(null)}
        message={pdfNotice} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Stack>
  );
}

function DialogKpi({ label, value, sub, accent }: {
  label: string; value: string; sub?: string; accent?: 'success';
}) {
  return (
    <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body1" fontWeight={700}
        color={accent === 'success' ? 'success.main' : 'text.primary'}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Box>
  );
}
